// Angular
import { ActivatedRouteSnapshot, Data, Routes } from '@angular/router';

// Local
import {
  canDeactivateGuard,
  canEmulateUser,
  canModifyOrg,
  homeDispatchGuard,
  isAttorneyUserGuard,
  isClerkUserGuard,
  isForm50AgentUserGuard,
  isInternalUser,
  isJudgeReviewUserGuard,
  judgeChambersEnabledGuard,
  judgeChambersRedirectGuard,
} from '@core/guards';
import {
  clerkReviewDataResolver,
  clerkReviewResolver,
} from '@feature/cr/clerk-review-resolver';
import { documentHistoryResolver } from '@app/components/document/document-history/document-history.resolver';
import {
  judgeReviewConfigResolver,
  judgeReviewDataResolver,
} from '@feature/jr/shell/judge-review.resolver';
import { judgeReviewReceiptGuard } from '@feature/jr/shell/judge-review-receipt.guard';
import { jrVerifyUnrepresentedGuard } from '@feature/jr/recipients/jr-verify-unrepresented.guard';
import { judgeReviewListResolver } from '@feature/jrl';
import { judgeChambersListResolver } from './app/pages/judge-review/chambers/judge-chambers-list.resolver';
import { organizationUsersResolver } from './app/pages/preferences/preferences.resolver';

/**
 * An enum of the available routes used for navigation.
 */
export enum ROUTES {
  HOME = 'home',
  ATTORNEY_DASHBOARD = 'home/attorney',
  CLERK_DASHBOARD = 'home/clerk',
  JUDGE_DASHBOARD = 'home/judge',
  FORM_50_AGENT_DASHBOARD = 'home/form50-agent',
  MAILBOX = 'mailbox',
  REJECTED_ITEMS = 'rejected-items',
  CASE_HISTORY = 'case-history',
  FORBIDDEN = 'forbidden',
  CLERK_REVIEW_LIST = 'clerk-review-list',
  CLERK_REVIEW = 'clerk-review',
  BATCH_REVIEW_LIST = 'batch-review-list',
  BATCH_REVIEW = 'batch-review',
  JUDGE_REVIEW_LIST = 'judge-review-list',
  JUDGE_CHAMBERS = 'judge-chambers',
  JUDGE_REVIEW = 'judge-review',
  JUDGE_REVIEW_LOAD_ERROR = 'judge-review-load-error',
  FILING = 'filing',
  CASE_MANAGEMENT = 'case-management',
  TRIALBINDER = 'trialbinder',
  MESSAGEBOARD = 'messageboard',
  PREFERENCES = 'preferences',
  MY_INFORMATION = 'my-information',
  NOTIFICATION_OPTIONS = 'notification-options',
  SAVED_TRANSACTION_ACCESS = 'saved-transaction-access',
  LOGOUT = 'logout',
  SYSTEM_ADMIN = 'system',
  EMULATE = 'emulate',
}

/**
 * An enum for the available router outlets.
 */
export enum ROUTER_OUTLETS {
  SIDEPANEL_INFO = 'sidepanel-info',
  SIDEPANEL_PARTY_SEARCH = 'sidepanel-party-search',
  SIDEPANEL_DOCUMENT_HISTORY = 'document-history',
  SIDEPANEL_AI_CLERK_REVIEW = 'ai-clerk-review',
  SIDEPANEL_ADD_PARTY = 'sidepanel-add-party',
  SIDEPANEL_ADD_REPRESENTATION = 'sidepanel-add-representation',
  SIDEPANEL_MANAGE_ALIASES = 'sidepanel-manage-aliases',
  SIDEPANEL_ATTORNEY_SEARCH = 'sidepanel-attorney-search',
  SIDEPANEL_ADD_RECIPIENT = 'sidepanel-add-recipient',
  SIDEPANEL_ADD_SERVICE_PARTY = 'sidepanel-add-service-party',
  SIDEPANEL_CHECK_ADDRESS = 'sidepanel-check-address',
  SIDEPANEL_MULTICASE_DETAILS = 'sidepanel-multicase-details',
  SIDEPANEL_ADD_PARTY_TO_SERVE = 'sidepanel-add-party-to-serve',
  SIDEPANEL_TRANSACTION_VIEW = 'sidepanel-transaction-view',
  SIDEPANEL_PRIVATE_UPLOAD = 'sidepanel-private-upload',
  SIDEPANEL_CASE_HISTORY = 'sidepanel-case-history',
  SIDEPANEL_CASE_INFORMATION = 'case-information',
  SIDEPANEL_TRANSACTION_RECIPIENTS = 'sidepanel-transaction-recipients',
  SIDEPANEL_ESCALATE_TRANSACTION = 'sidepanel-escalate-transaction',
  SIDEPANEL_MANAGE_CHAMBER = 'sidepanel-manage-chamber',
  SIDEPANEL_LINKED_DOCUMENTS = 'sidepanel-linked-documents',
  SIDEPANEL_CHAMBER_NOTES = 'sidepanel-chamber-notes',
}

/**
 * Routes configuration for the application.
 */
export const APP_ROUTES: Routes = [
  {
    path: '',
    // ToDo: Disabled until I can correctly get the redirect to work.
    // canActivateChild: [canActivateChild],
    children: [
      {
        path: ROUTES.NOTIFICATION_OPTIONS,
        title: 'Notification Options',
        loadComponent: () =>
          import(
            './app/pages/notification-options/notification-options.component'
          ).then(m => m.NotificationOptionsComponent),
        outlet: ROUTER_OUTLETS.SIDEPANEL_INFO,
        resolve: {
          organizationUsers: organizationUsersResolver,
        },
        canDeactivate: [canDeactivateGuard],
      },
      {
        path: ROUTES.MY_INFORMATION,
        title: 'My Information',
        loadComponent: () =>
          import('./app/pages/my-information/my-information.component').then(
            m => m.MyInformationComponent,
          ),
        outlet: ROUTER_OUTLETS.SIDEPANEL_INFO,
        canDeactivate: [canDeactivateGuard],
      },
      {
        path: ROUTES.SAVED_TRANSACTION_ACCESS,
        title: 'My Saved Transactions',
        loadComponent: () =>
          import(
            './app/pages/saved-transaction-access/saved-transaction-access.component'
          ).then(m => m.SavedTransactionAccessComponent),
        outlet: ROUTER_OUTLETS.SIDEPANEL_INFO,
        canDeactivate: [canDeactivateGuard],
      },
      {
        path: ROUTES.HOME,
        title: 'Home',
        canActivate: [homeDispatchGuard],
        loadComponent: () => import('./app/pages/home/home.component'),
        data: {
          breadcrumb: 'Home',
        },
      },
      {
        path: ROUTES.ATTORNEY_DASHBOARD,
        title: 'Home',
        canActivate: [isAttorneyUserGuard],
        loadComponent: () =>
          import(
            './app/pages/home/attorney-dashboard/attorney-dashboard.component'
          ),
        data: {
          breadcrumb: 'Home',
        },
        children: [
          {
            path: 'transaction/:filingUniqueId',
            loadComponent: () =>
              import(
                './app/shared/components/transaction-view-sidecar/transaction-view-sidecar.component'
              ).then(m => m.TransactionViewSidecarComponent),
            pathMatch: 'full',
            data: {
              breadcrumb: '',
            },
            outlet: ROUTER_OUTLETS.SIDEPANEL_TRANSACTION_VIEW,
          },
          {
            path: 'private-upload/:caseUniqueId',
            loadComponent: () =>
              import(
                './app/pages/home/attorney-dashboard/private-upload-sidecar/private-upload-sidecar.component'
              ).then(m => m.PrivateUploadSidecarComponent),
            pathMatch: 'full',
            data: {
              breadcrumb: '',
            },
            outlet: ROUTER_OUTLETS.SIDEPANEL_PRIVATE_UPLOAD,
          },
          {
            path: 'case-history/:caseId',
            loadComponent: () =>
              import(
                './app/pages/home/attorney-dashboard/case-history-sidecar/case-history-sidecar.component'
              ).then(m => m.CaseHistorySidecarComponent),
            pathMatch: 'full',
            data: {
              breadcrumb: '',
            },
            outlet: ROUTER_OUTLETS.SIDEPANEL_CASE_HISTORY,
          },
          {
            path: 'case-information/:caseUniqueId',
            loadComponent: () =>
              import(
                './app/shared/components/case-info-sidecar/case-info-sidecar.component'
              ).then(m => m.CaseInfoSidecarComponent),
            pathMatch: 'full',
            data: {
              breadcrumb: '',
            },
            outlet: ROUTER_OUTLETS.SIDEPANEL_CASE_INFORMATION,
          },
        ],
      },
      {
        path: ROUTES.FORM_50_AGENT_DASHBOARD,
        title: 'Home',
        canActivate: [isForm50AgentUserGuard],
        loadComponent: () =>
          import(
            './app/pages/home/form50-agent-dashboard/form50-agent-dashboard.component'
          ),
        data: {
          breadcrumb: 'Home',
        },
        children: [
          {
            path: 'transaction/:filingUniqueId',
            loadComponent: () =>
              import(
                './app/shared/components/transaction-view-sidecar/transaction-view-sidecar.component'
              ).then(m => m.TransactionViewSidecarComponent),
            pathMatch: 'full',
            data: {
              breadcrumb: '',
            },
            outlet: ROUTER_OUTLETS.SIDEPANEL_TRANSACTION_VIEW,
          },
          {
            path: 'private-upload/:caseUniqueId',
            loadComponent: () =>
              import(
                './app/pages/home/attorney-dashboard/private-upload-sidecar/private-upload-sidecar.component'
              ).then(m => m.PrivateUploadSidecarComponent),
            pathMatch: 'full',
            data: {
              breadcrumb: '',
            },
            outlet: ROUTER_OUTLETS.SIDEPANEL_PRIVATE_UPLOAD,
          },
          {
            path: 'case-history/:caseId',
            loadComponent: () =>
              import(
                './app/pages/home/attorney-dashboard/case-history-sidecar/case-history-sidecar.component'
              ).then(m => m.CaseHistorySidecarComponent),
            pathMatch: 'full',
            data: {
              breadcrumb: '',
            },
            outlet: ROUTER_OUTLETS.SIDEPANEL_CASE_HISTORY,
          },
          {
            path: 'case-information/:caseUniqueId',
            loadComponent: () =>
              import(
                './app/shared/components/case-info-sidecar/case-info-sidecar.component'
              ).then(m => m.CaseInfoSidecarComponent),
            pathMatch: 'full',
            data: {
              breadcrumb: '',
            },
            outlet: ROUTER_OUTLETS.SIDEPANEL_CASE_INFORMATION,
          },
        ],
      },
      {
        path: ROUTES.CLERK_DASHBOARD,
        title: 'Home',
        canActivate: [isClerkUserGuard],
        loadComponent: () =>
          import('./app/pages/home/clerk-dashboard/clerk-dashboard.component'),
        data: {
          breadcrumb: 'Home',
        },
        children: [
          {
            path: 'transaction/:filingUniqueId',
            loadComponent: () =>
              import(
                './app/shared/components/transaction-view-sidecar/transaction-view-sidecar.component'
              ).then(m => m.TransactionViewSidecarComponent),
            pathMatch: 'full',
            data: {
              breadcrumb: '',
            },
            outlet: ROUTER_OUTLETS.SIDEPANEL_TRANSACTION_VIEW,
          },
          {
            path: 'private-upload/:caseUniqueId',
            loadComponent: () =>
              import(
                './app/pages/home/attorney-dashboard/private-upload-sidecar/private-upload-sidecar.component'
              ).then(m => m.PrivateUploadSidecarComponent),
            pathMatch: 'full',
            data: {
              breadcrumb: '',
            },
            outlet: ROUTER_OUTLETS.SIDEPANEL_PRIVATE_UPLOAD,
          },
          {
            path: 'case-history/:caseId',
            loadComponent: () =>
              import(
                './app/pages/home/attorney-dashboard/case-history-sidecar/case-history-sidecar.component'
              ).then(m => m.CaseHistorySidecarComponent),
            pathMatch: 'full',
            data: {
              breadcrumb: '',
            },
            outlet: ROUTER_OUTLETS.SIDEPANEL_CASE_HISTORY,
          },
        ],
      },
      {
        path: ROUTES.JUDGE_DASHBOARD,
        title: 'Home',
        // Judge + clerk-on-behalf: same eligibility as the judge review flow.
        canActivate: [isJudgeReviewUserGuard],
        loadComponent: () =>
          import('./app/pages/home/judge-dashboard/judge-dashboard.component'),
        data: {
          breadcrumb: 'Home',
        },
        children: [
          {
            path: 'transaction/:filingUniqueId',
            loadComponent: () =>
              import(
                './app/shared/components/transaction-view-sidecar/transaction-view-sidecar.component'
              ).then(m => m.TransactionViewSidecarComponent),
            pathMatch: 'full',
            data: {
              breadcrumb: '',
            },
            outlet: ROUTER_OUTLETS.SIDEPANEL_TRANSACTION_VIEW,
          },
          {
            path: 'private-upload/:caseUniqueId',
            loadComponent: () =>
              import(
                './app/pages/home/attorney-dashboard/private-upload-sidecar/private-upload-sidecar.component'
              ).then(m => m.PrivateUploadSidecarComponent),
            pathMatch: 'full',
            data: {
              breadcrumb: '',
            },
            outlet: ROUTER_OUTLETS.SIDEPANEL_PRIVATE_UPLOAD,
          },
          {
            path: 'case-history/:caseId',
            loadComponent: () =>
              import(
                './app/pages/home/attorney-dashboard/case-history-sidecar/case-history-sidecar.component'
              ).then(m => m.CaseHistorySidecarComponent),
            pathMatch: 'full',
            data: {
              breadcrumb: '',
            },
            outlet: ROUTER_OUTLETS.SIDEPANEL_CASE_HISTORY,
          },
        ],
      },
      {
        path: ROUTES.MAILBOX,
        title: 'Mailbox',
        loadChildren: () => import('./app/pages/mailbox/mailbox.routes'),
        data: {
          breadcrumb: 'Mailbox',
        },
      },
      {
        // The legacy standalone Rejected Items route now lives under the Mailbox
        // feature (mailbox/rejected). Keep a redirect for existing bookmarks/links.
        path: ROUTES.REJECTED_ITEMS,
        redirectTo: 'mailbox/rejected',
        pathMatch: 'full',
      },
      {
        // NOTE: Case History is intentionally firm-wide, not attorney-gated
        // (unlike /home/attorney). Any authenticated user may reach it — e.g.
        // clerks and firm staff linking to a case's history by GUID. Per-case
        // access is enforced server-side by the case-history endpoints; the
        // route only loads the page shell.
        path: `${ROUTES.CASE_HISTORY}/:caseId`,
        title: 'Case History',
        loadComponent: () =>
          import('./app/pages/case-history/case-history.component'),
        data: {
          breadcrumb: 'Case History',
        },
        children: [
          {
            path: 'transaction/:filingUniqueId',
            loadComponent: () =>
              import(
                './app/shared/components/transaction-view-sidecar/transaction-view-sidecar.component'
              ).then(m => m.TransactionViewSidecarComponent),
            pathMatch: 'full',
            data: {
              breadcrumb: '',
            },
            outlet: ROUTER_OUTLETS.SIDEPANEL_TRANSACTION_VIEW,
          },
        ],
      },
      {
        path: ROUTES.CLERK_REVIEW_LIST,
        title: 'Clerk Review',
        loadComponent: () =>
          import('./app/pages/clerk-review-list/clerk-review-list.component'),
        data: {
          breadcrumb: 'Clerk Review',
        },
        children: [
          {
            path: 'case-information/:caseId',
            loadComponent: () =>
              import(
                './app/pages/clerk-review-list/case-information/case-information.component'
              ).then(m => m.CaseInformationComponent),
            outlet: 'case-information',
            data: {
              breadcrumb: 'Case Information',
            },
          },
          {
            // Chamber notes shown in clerk review (courts with judge chambers).
            path: 'notes/:targetType/:targetId',
            loadComponent: () =>
              import(
                './app/pages/judge-review/chambers/chamber-notes-sidecar/chamber-notes-sidecar.component'
              ).then(m => m.ChamberNotesSidecarComponent),
            pathMatch: 'full',
            data: {
              breadcrumb: '',
            },
            outlet: ROUTER_OUTLETS.SIDEPANEL_CHAMBER_NOTES,
          },
          {
            // Chamber notifications (mentions in chamber notes).
            path: 'notifications',
            loadComponent: () =>
              import(
                './app/pages/judge-review/chambers/chamber-notifications-sidecar/chamber-notifications-sidecar.component'
              ).then(m => m.ChamberNotificationsSidecarComponent),
            pathMatch: 'full',
            data: { breadcrumb: '' },
            outlet: ROUTER_OUTLETS.SIDEPANEL_CHAMBER_NOTES,
          },
        ],
      },
      {
        // Batches are the other half of a court's review queue and are deliberately separate from
        // the single-transaction list, which excludes batched filings server-side.
        path: ROUTES.BATCH_REVIEW_LIST,
        title: 'Batches for Clerk Review',
        loadComponent: () =>
          import('./app/pages/batch-review-list/batch-review-list.component'),
        data: {
          breadcrumb: 'Batches for Clerk Review',
        },
      },
      {
        path: `${ROUTES.BATCH_REVIEW}/:bulkFilingId`,
        title: 'Batch Review',
        loadComponent: () =>
          import('./app/pages/batch-review/batch-review.component'),
        data: {
          breadcrumb: (data: Data, route: ActivatedRouteSnapshot) => {
            const bulkFilingId = route?.params?.['bulkFilingId'];
            return bulkFilingId
              ? ['Batches for Clerk Review', `Batch ${bulkFilingId}`]
              : ['Batches for Clerk Review'];
          },
        },
      },
      {
        path: ROUTES.JUDGE_REVIEW_LIST,
        title: 'Judge Review',
        canActivate: [isJudgeReviewUserGuard, judgeChambersRedirectGuard],
        loadComponent: () =>
          import('./app/pages/judge-review/list/judge-review-list.component'),
        data: {
          breadcrumb: 'Judge Review',
        },
        resolve: {
          judgeReviewData: judgeReviewListResolver,
        },
        children: [
          {
            path: 'transaction/:filingUniqueId',
            loadComponent: () =>
              import(
                './app/shared/components/transaction-view-sidecar/transaction-view-sidecar.component'
              ).then(m => m.TransactionViewSidecarComponent),
            pathMatch: 'full',
            data: { breadcrumb: '' },
            outlet: ROUTER_OUTLETS.SIDEPANEL_TRANSACTION_VIEW,
          },
          {
            path: 'case-history/:caseId',
            loadComponent: () =>
              import(
                './app/pages/home/attorney-dashboard/case-history-sidecar/case-history-sidecar.component'
              ).then(m => m.CaseHistorySidecarComponent),
            pathMatch: 'full',
            data: { breadcrumb: '' },
            outlet: ROUTER_OUTLETS.SIDEPANEL_CASE_HISTORY,
          },
        ],
      },
      {
        path: ROUTES.JUDGE_CHAMBERS,
        title: 'Judge Review',
        canActivate: [isJudgeReviewUserGuard, judgeChambersEnabledGuard],
        loadComponent: () =>
          import('./app/pages/judge-review/chambers/judge-chambers-list.component'),
        data: {
          breadcrumb: 'Judge Review',
        },
        resolve: {
          judgeReviewData: judgeReviewListResolver,
          judgeChambers: judgeChambersListResolver,
        },
        children: [
          {
            path: 'tags',
            loadComponent: () =>
              import(
                './app/pages/judge-review/chambers/chamber-tags-sidecar/chamber-tags-sidecar.component'
              ).then(m => m.ChamberTagsSidecarComponent),
            pathMatch: 'full',
            data: {
              breadcrumb: '',
            },
            outlet: ROUTER_OUTLETS.SIDEPANEL_MANAGE_CHAMBER,
          },
          {
            path: 'notes/:targetType/:targetId',
            loadComponent: () =>
              import(
                './app/pages/judge-review/chambers/chamber-notes-sidecar/chamber-notes-sidecar.component'
              ).then(m => m.ChamberNotesSidecarComponent),
            pathMatch: 'full',
            data: {
              breadcrumb: '',
            },
            outlet: ROUTER_OUTLETS.SIDEPANEL_MANAGE_CHAMBER,
          },
          {
            path: 'case-information/:caseUniqueId',
            loadComponent: () =>
              import(
                './app/shared/components/case-info-sidecar/case-info-sidecar.component'
              ).then(m => m.CaseInfoSidecarComponent),
            pathMatch: 'full',
            data: { breadcrumb: 'Case Information' },
            outlet: ROUTER_OUTLETS.SIDEPANEL_CASE_INFORMATION,
          },
          {
            path: 'notifications',
            loadComponent: () =>
              import(
                './app/pages/judge-review/chambers/chamber-notifications-sidecar/chamber-notifications-sidecar.component'
              ).then(m => m.ChamberNotificationsSidecarComponent),
            pathMatch: 'full',
            data: { breadcrumb: '' },
            outlet: ROUTER_OUTLETS.SIDEPANEL_MANAGE_CHAMBER,
          },
        ],
      },
      {
        path: `${ROUTES.JUDGE_REVIEW}/:filingUniqueId`,
        title: 'Judge Review — Transaction',
        canActivate: [isJudgeReviewUserGuard],
        loadComponent: () =>
          import(
            './app/pages/judge-review/transaction-detail/judge-review-transaction-detail.component'
          ),
        resolve: {
          judgeReviewConfiguration: judgeReviewConfigResolver,
          judgeReviewData: judgeReviewDataResolver,
        },
        data: {
          breadcrumb: (data: Data, route: ActivatedRouteSnapshot) => {
            const filingUniqueId = route?.params?.['filingUniqueId'];
            const displayId =
              data?.['judgeReviewData']?.reviewedFilingId ?? filingUniqueId;
            return filingUniqueId
              ? ['Judge Review', `Transaction ${displayId}`]
              : ['Judge Review'];
          },
        },
        children: [
          {
            path: 'history/:documentId',
            loadComponent: () =>
              import(
                './app/components/document/document-history/document-history.component'
              ).then(m => m.DocumentHistoryComponent),
            pathMatch: 'full',
            resolve: { documentHistory: documentHistoryResolver },
            data: { breadcrumb: '' },
            outlet: ROUTER_OUTLETS.SIDEPANEL_DOCUMENT_HISTORY,
          },
          {
            path: 'case-history/:caseId',
            loadComponent: () =>
              import(
                './app/pages/home/attorney-dashboard/case-history-sidecar/case-history-sidecar.component'
              ).then(m => m.CaseHistorySidecarComponent),
            pathMatch: 'full',
            data: { breadcrumb: '' },
            outlet: ROUTER_OUTLETS.SIDEPANEL_CASE_HISTORY,
          },
          {
            path: 'case-information/:caseUniqueId',
            loadComponent: () =>
              import(
                './app/shared/components/case-info-sidecar/case-info-sidecar.component'
              ).then(m => m.CaseInfoSidecarComponent),
            pathMatch: 'full',
            data: { breadcrumb: 'Case Information' },
            outlet: ROUTER_OUTLETS.SIDEPANEL_CASE_INFORMATION,
          },
          {
            path: 'linked-documents/:documentId',
            loadComponent: () =>
              import(
                './app/pages/judge-review/linked-documents-sidecar/judge-review-linked-documents-sidecar.component'
              ).then(m => m.JudgeReviewLinkedDocumentsSidecarComponent),
            pathMatch: 'full',
            data: { breadcrumb: '' },
            outlet: ROUTER_OUTLETS.SIDEPANEL_LINKED_DOCUMENTS,
          },
          {
            // Chamber notes (courts with judge chambers only).
            path: 'notes/:targetType/:targetId',
            loadComponent: () =>
              import(
                './app/pages/judge-review/chambers/chamber-notes-sidecar/chamber-notes-sidecar.component'
              ).then(m => m.ChamberNotesSidecarComponent),
            pathMatch: 'full',
            data: { breadcrumb: '' },
            outlet: ROUTER_OUTLETS.SIDEPANEL_MANAGE_CHAMBER,
          },
        ],
      },
      {
        path: `${ROUTES.JUDGE_REVIEW}/:filingUniqueId/review-sign`,
        title: 'Judge Review — Review & Sign',
        loadComponent: () =>
          import('./app/pages/judge-review/shell/judge-review.component'),
        canActivate: [isJudgeReviewUserGuard],
        canDeactivate: [canDeactivateGuard],
        resolve: {
          judgeReviewConfiguration: judgeReviewConfigResolver,
          judgeReviewData: judgeReviewDataResolver,
        },
        data: {
          breadcrumb: (data: Data, route: ActivatedRouteSnapshot) => {
            const filingUniqueId = route?.params?.['filingUniqueId'];
            const displayId =
              data?.['judgeReviewData']?.reviewedFilingId ?? filingUniqueId;
            return filingUniqueId
              ? ['Judge Review', `Transaction ${displayId}`, 'Review & Sign']
              : ['Judge Review', 'Review & Sign'];
          },
        },
      },
      {
        path: `${ROUTES.JUDGE_REVIEW}/:filingUniqueId/confirm`,
        title: 'Judge Review — Confirmation',
        canActivate: [isJudgeReviewUserGuard],
        loadComponent: () =>
          import(
            './app/pages/judge-review/confirm/judge-review-confirm.component'
          ),
        resolve: {
          judgeReviewConfiguration: judgeReviewConfigResolver,
          judgeReviewData: judgeReviewDataResolver,
        },
        data: {
          breadcrumb: (data: Data, route: ActivatedRouteSnapshot) => {
            const filingUniqueId = route?.params?.['filingUniqueId'];
            const displayId =
              data?.['judgeReviewData']?.reviewedFilingId ?? filingUniqueId;
            return filingUniqueId
              ? ['Judge Review', `Transaction ${displayId}`, 'Confirmation']
              : ['Judge Review', 'Confirmation'];
          },
        },
      },
      {
        path: `${ROUTES.JUDGE_REVIEW}/:filingUniqueId/recipients`,
        title: 'Judge Review — Custom Recipients',
        canActivate: [isJudgeReviewUserGuard],
        canDeactivate: [jrVerifyUnrepresentedGuard],
        loadComponent: () =>
          import(
            './app/pages/judge-review/recipients/judge-review-recipients.component'
          ),
        resolve: {
          judgeReviewConfiguration: judgeReviewConfigResolver,
          judgeReviewData: judgeReviewDataResolver,
        },
        data: {
          breadcrumb: (data: Data, route: ActivatedRouteSnapshot) => {
            const filingUniqueId = route?.params?.['filingUniqueId'];
            const displayId =
              data?.['judgeReviewData']?.reviewedFilingId ?? filingUniqueId;
            return filingUniqueId
              ? [
                  'Judge Review',
                  `Transaction ${displayId}`,
                  'Custom Recipients',
                ]
              : ['Judge Review', 'Custom Recipients'];
          },
        },
        children: [
          {
            path: 'add-recipient',
            loadComponent: () =>
              import(
                './app/pages/judge-review/recipients/jr-add-recipient-sidecar/jr-add-recipient-sidecar.component'
              ).then(m => m.JrAddRecipientSidecarComponent),
            outlet: ROUTER_OUTLETS.SIDEPANEL_ADD_RECIPIENT,
            data: { breadcrumb: '' },
          },
        ],
      },
      {
        path: `${ROUTES.JUDGE_REVIEW}/:filingUniqueId/address`,
        title: 'Judge Review — Mailing Addresses',
        canActivate: [isJudgeReviewUserGuard],
        loadComponent: () =>
          import('./app/pages/judge-review/address/jr-check-address.component'),
        resolve: {
          judgeReviewConfiguration: judgeReviewConfigResolver,
          judgeReviewData: judgeReviewDataResolver,
        },
        data: {
          breadcrumb: (data: Data, route: ActivatedRouteSnapshot) => {
            const filingUniqueId = route?.params?.['filingUniqueId'];
            const displayId =
              data?.['judgeReviewData']?.reviewedFilingId ?? filingUniqueId;
            return filingUniqueId
              ? [
                  'Judge Review',
                  `Transaction ${displayId}`,
                  'Mailing Addresses',
                ]
              : ['Judge Review', 'Mailing Addresses'];
          },
        },
      },
      {
        path: `${ROUTES.JUDGE_REVIEW}/:filingUniqueId/receipt`,
        title: 'Judge Review — Receipt',
        canActivate: [isJudgeReviewUserGuard, judgeReviewReceiptGuard],
        loadComponent: () =>
          import(
            './app/pages/judge-review/receipt/judge-review-receipt.component'
          ),
        data: {
          breadcrumb: (data: Data, route: ActivatedRouteSnapshot) => {
            const filingUniqueId = route?.params?.['filingUniqueId'];
            // Mirror the sibling routes' friendly-id fallback. The receipt route
            // has no resolver, so `judgeReviewData` is typically absent here and
            // this degrades to the filing GUID — kept consistent so a future
            // resolver would surface the friendly id automatically.
            const displayId =
              data?.['judgeReviewData']?.reviewedFilingId ?? filingUniqueId;
            return filingUniqueId
              ? ['Judge Review', `Transaction ${displayId}`, 'Receipt']
              : ['Judge Review', 'Receipt'];
          },
        },
      },
      {
        path: ROUTES.JUDGE_REVIEW_LOAD_ERROR,
        title: 'Judge Review — Load Error',
        canActivate: [isJudgeReviewUserGuard],
        loadComponent: () =>
          import(
            './app/pages/judge-review/load-error/judge-review-load-error.component'
          ),
      },
      {
        path: `${ROUTES.CLERK_REVIEW}/:filingUniqueId`,
        title: 'Clerk Review',
        loadComponent: () =>
          import('./app/pages/clerk-review/clerk-review.component'),
        resolve: {
          clerkReviewConfiguration: clerkReviewResolver,
          clerkReviewData: clerkReviewDataResolver,
        },
        data: {
          breadcrumb: (data: any, route: any) => {
            const filingId =
              data?.clerkReviewData?.filingId ?? route?.params?.filingUniqueId;
            return filingId
              ? ['Clerk Review', `Transaction ${filingId}`]
              : ['Clerk Review'];
          },
        },
        children: [
          {
            path: 'history/:documentId',
            loadComponent: () =>
              import(
                './app/components/document/document-history/document-history.component'
              ).then(m => m.DocumentHistoryComponent),
            pathMatch: 'full',
            resolve: { documentHistory: documentHistoryResolver },
            data: {
              breadcrumb: '',
            },
            outlet: ROUTER_OUTLETS.SIDEPANEL_DOCUMENT_HISTORY,
          },
          {
            path: 'ai-review/:documentId',
            loadComponent: () =>
              import(
                './app/pages/clerk-review/ai-clerk-review-sidecar/ai-clerk-review-sidecar.component'
              ).then(m => m.AiClerkReviewSidecarComponent),
            pathMatch: 'full',
            data: {
              breadcrumb: '',
            },
            outlet: ROUTER_OUTLETS.SIDEPANEL_AI_CLERK_REVIEW,
          },
          {
            path: 'manage-aliases',
            loadComponent: () =>
              import(
                './app/pages/clerk-review/clerk-review-party/manage-aliases/clerk-review-manage-aliases.component'
              ).then(m => m.ClerkReviewManageAliasesComponent),
            pathMatch: 'full',
            data: {
              breadcrumb: '',
            },
            outlet: ROUTER_OUTLETS.SIDEPANEL_MANAGE_ALIASES,
          },
          {
            path: 'transaction-recipients',
            loadComponent: () =>
              import(
                './app/pages/clerk-review/transaction-recipients-sidecar/transaction-recipients-sidecar.component'
              ).then(m => m.TransactionRecipientsSidecarComponent),
            pathMatch: 'full',
            data: {
              breadcrumb: '',
            },
            outlet: ROUTER_OUTLETS.SIDEPANEL_TRANSACTION_RECIPIENTS,
          },
          {
            path: 'escalate',
            loadComponent: () =>
              import(
                './app/pages/clerk-review/escalate-transaction-sidecar/escalate-transaction-sidecar.component'
              ).then(m => m.EscalateTransactionSidecarComponent),
            pathMatch: 'full',
            data: {
              breadcrumb: '',
            },
            outlet: ROUTER_OUTLETS.SIDEPANEL_ESCALATE_TRANSACTION,
          },
          {
            // Chamber notes shown in clerk review (courts with judge chambers).
            path: 'notes/:targetType/:targetId',
            loadComponent: () =>
              import(
                './app/pages/judge-review/chambers/chamber-notes-sidecar/chamber-notes-sidecar.component'
              ).then(m => m.ChamberNotesSidecarComponent),
            pathMatch: 'full',
            data: {
              breadcrumb: '',
            },
            outlet: ROUTER_OUTLETS.SIDEPANEL_CHAMBER_NOTES,
          },
        ],
      },
      {
        path: ROUTES.FILING,
        title: 'Filing Module',
        data: {
          breadcrumb: 'Filing Module',
        },
        loadChildren: () => import('./app/pages/filing/filing.routes'),
      },
      {
        path: ROUTES.CASE_MANAGEMENT,
        title: 'Case & Party Management',
        data: {
          breadcrumb: 'Case & Party Management',
        },
        loadChildren: () =>
          import(
            './app/pages/track-and-manage/case-management/case-management.routes'
          ),
      },
      {
        path: ROUTES.TRIALBINDER,
        title: 'eBinder',
        loadChildren: () => import('./app/trialbinder/trialbinder.routes'),
      },
      {
        path: ROUTES.MESSAGEBOARD,
        title: 'Message Boards',
        data: {
          breadcrumb: 'Message Boards',
        },
        children: [
          {
            path: '',
            loadComponent: () =>
              import('./app/pages/messageboard/messageboard.component'),
            pathMatch: 'full',
            data: {
              breadcrumb: '',
            },
          },
          {
            path: 'subjects/:forumID',
            loadComponent: () =>
              import('./app/pages/subjects/subjects.component'),
            data: {
              breadcrumb: 'Subjects',
            },
          },
        ],
      },
      {
        path: ROUTES.PREFERENCES,
        title: 'Preferences',
        loadChildren: () =>
          import('./app/pages/preferences/preferences.routes'),
        canActivate: [canModifyOrg],
      },
      {
        path: ROUTES.LOGOUT,
        title: '',
        loadComponent: () => import('./app/pages/logout/logout.component'),
        data: {
          breadcrumb: '',
        },
      },
      {
        path: ROUTES.SYSTEM_ADMIN,
        title: 'System Administration',
        loadChildren: () =>
          import('./app/pages/system-admin/system-admin.routes'),
        data: {
          breadcrumb: 'System Administration',
        },
        canActivate: [isInternalUser],
      },
      {
        // The screen moved under System Administration; keep old links working.
        path: 'manage-court-house',
        redirectTo: `${ROUTES.SYSTEM_ADMIN}/manage-court-house`,
        pathMatch: 'full',
      },
      {
        path: ROUTES.EMULATE,
        title: 'Emulate a User',
        loadComponent: () =>
          import('./app/pages/emulate-user/emulate-user.component').then(
            m => m.EmulateUserComponent,
          ),
        data: {
          breadcrumb: 'Emulate a User',
        },
        // Capability-gated only: the server decides who may emulate (System Admins and
        // Customer Service both qualify), so this route must not be limited to the
        // System-Administration user types.
        canActivate: [canEmulateUser],
      },
      {
        path: ROUTES.FORBIDDEN,
        title: 'Forbidden',
        loadComponent: () =>
          import('./app/pages/forbidden/forbidden.component'),
        data: {
          breadcrumb: 'Forbidden',
        },
      },
      { path: '', redirectTo: 'home', pathMatch: 'full' },
      { path: '**', redirectTo: 'home', pathMatch: 'full' },
    ],
  },
];

/**
 * An interface to wrap http responses.
 */
export interface Result<T> {
  data: T | undefined;
  error?: string;
}
